/**
 * The public demo's "server", running in the visitor's own tab.
 *
 * Every /api request the app makes is answered here, by the very code the
 * Windows app uses when it has no connection (OfflineApi / OfflineBusiness in
 * apps/desktop), over a copy of the Demo Clinic held in memory. Nothing a
 * visitor does is sent anywhere: there is no account, no session and no
 * database behind the demo, so any number of people can use it at once and
 * none of them can change what the next one sees.
 *
 * What the offline engine does not build itself (reports, settings, a
 * dentist's statement, …) is answered from recorded responses and is
 * read-only. Changes live in this tab (sessionStorage) until it is closed or
 * the visitor presses Reset.
 */
import { OfflineApi } from 'virtual:oxy-offline-api';
import { MemoryStore, type Row } from './memory-store';

export const DEMO_LOGIN = { email: 'demo@oxyflows.com', password: 'demo' } as const;

const DELTAS_KEY = 'oxy-demo-deltas-v1';
const SESSION_KEY = 'oxy-demo-signed-in';
const DAY = 86_400_000;

interface DemoData {
  version: number;
  anchor: string;
  me: Record<string, unknown> & { user: Record<string, unknown> };
  snapshot: { clinicId: string; userId: string; users: Row[]; tables: Record<string, Row[]> };
  responses: Record<string, unknown>;
  files: Record<string, { file: string; type: string }>;
}

interface RecordInput {
  entity: string;
  entityId: string;
  action: string;
  payload: Record<string, unknown>;
  rows: { entity: string; id: string; data: Row; patientId?: string | null; serverVersion?: number }[];
  deletes?: { entity: string; id: string }[];
}

type Delta = { rows: RecordInput['rows']; deletes: NonNullable<RecordInput['deletes']> } | { patch: { entity: string; id: string; data: Row } };

const READ_ONLY = {
  en: 'This part is read-only in the demo. In a real clinic it works as you would expect.',
  ar: 'هذا الجزء للعرض فقط في النسخة التجريبية. يعمل في العيادة الحقيقية كما هو متوقع.',
};

/**
 * Move every date in the copy so the demo's "today" is the visitor's today
 * (in whole weeks, so a clinic closed on Fridays stays closed on Fridays).
 */
function shiftDates(text: string, anchor: string): string {
  const today = new Date();
  const todayUtc = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  const days = Math.round((todayUtc - Date.parse(`${anchor}T00:00:00Z`)) / DAY / 7) * 7;
  if (days === 0) return text;
  const months = Math.round(days / 30.44);
  return text
    .replace(/\b(20\d{2})-(\d{2})-(\d{2})\b/g, (_m, y: string, mo: string, d: string) => new Date(Date.UTC(+y, +mo - 1, +d) + days * DAY).toISOString().slice(0, 10))
    .replace(/"(20\d{2})-(\d{2})"/g, (_m, y: string, mo: string) => {
      const t = new Date(Date.UTC(+y, +mo - 1 + months, 1));
      return `"${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}"`;
    });
}

class DemoEngine {
  readonly deviceId = '00000000-0000-4000-8000-00000000d3e0';
  readonly active: { userId: string; clinicId: string; email: string; clinicName: string; lastUsedAt: string };
  onWrite: (d: Delta) => void = () => undefined;

  constructor(
    readonly store: MemoryStore,
    userId: string,
    clinicId: string,
  ) {
    this.active = { userId, clinicId, email: DEMO_LOGIN.email, clinicName: 'Demo Clinic', lastUsedAt: new Date().toISOString() };
  }

  knownProfiles() {
    return [this.active];
  }

  open() {
    return this.store;
  }

  leaseVerdict() {
    return { ok: true, claims: null, daysRemaining: 365, level: 'ok', detail: null };
  }

  record(input: RecordInput) {
    const deletes = input.deletes ?? [];
    // Offline, an invoice waits for the server's number; the demo IS the server.
    for (const r of input.rows) {
      if (r.entity !== 'invoice') continue;
      r.data.provisional_ref = null;
      if (r.data.status === 'posted' && !r.data.number) r.data.number = this.nextInvoiceNumber();
    }
    this.apply(input.rows, deletes);
    this.onWrite({ rows: input.rows, deletes });
    return { operationId: crypto.randomUUID(), entity: input.entity, entityId: input.entityId, action: input.action, payload: input.payload };
  }

  nextInvoiceNumber(): string {
    const year = new Date().getFullYear();
    const used = this.store.rows('invoice').map((i) => String(i.number ?? '').match(new RegExp(`^INV-${year}-(\\d+)$`))?.[1]).filter(Boolean).map(Number);
    return `INV-${year}-${String(Math.max(0, ...used) + 1).padStart(5, '0')}`;
  }

  apply(rows: RecordInput['rows'], deletes: NonNullable<RecordInput['deletes']>) {
    for (const r of rows) this.store.upsertRow(r.entity, r.id, r.data, { patientId: r.patientId, serverVersion: Number(r.data.version ?? 1), provisional: false });
    for (const d of deletes) this.store.deleteRow(d.entity, d.id);
  }
}

export interface DemoResponse {
  status: number;
  type: string;
  body: string | Uint8Array | Blob | null;
}

export class DemoRuntime {
  private readonly store = new MemoryStore();
  private engine!: DemoEngine;
  private api!: OfflineApi;
  private data!: DemoData;
  private deltas: Delta[] = [];

  constructor(private readonly base: string) {}

  async load(): Promise<void> {
    const res = await fetch(`${this.base}data/demo.json.gz`, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`The demo data could not be loaded (${res.status}).`);
    // Unpacked here, unless something on the way already did.
    const packed = new Uint8Array(await res.arrayBuffer());
    const raw =
      packed[0] === 0x1f && packed[1] === 0x8b
        ? await new Response(new Blob([packed]).stream().pipeThrough(new DecompressionStream('gzip'))).text()
        : new TextDecoder().decode(packed);
    const anchor = raw.match(/"anchor":"(\d{4}-\d{2}-\d{2})"/)?.[1] ?? new Date().toISOString().slice(0, 10);
    this.data = JSON.parse(shiftDates(raw, anchor)) as DemoData;
    const { snapshot } = this.data;
    this.engine = new DemoEngine(this.store, snapshot.userId, snapshot.clinicId);
    this.engine.onWrite = (d) => this.remember(d);
    this.seed();
    // The engine also writes rows outside record() (patchRow): catch those too.
    const patch = this.store.patchRow.bind(this.store);
    this.store.patchRow = (entity, id, data) => {
      // Posting an invoice offline patches its status; here it gets its number too.
      if (entity === 'invoice' && data.status === 'posted' && !this.store.getRow('invoice', id)?.number) data = { ...data, number: this.engine.nextInvoiceNumber() };
      patch(entity, id, data);
      this.remember({ patch: { entity, id, data } });
    };
    this.api = new OfflineApi(this.engine as never, 'demo');
    try {
      if (sessionStorage.getItem(SESSION_KEY) === '1') this.signIn();
    } catch {
      /* private mode: start signed out */
    }
  }

  /** The copy as exported, then this tab's own changes on top. */
  private seed() {
    const { snapshot, responses, files } = this.data;
    for (const [entity, rows] of Object.entries(snapshot.tables)) for (const row of rows) this.store.upsertRow(entity, String(row.id), row);
    for (const u of snapshot.users) this.store.upsertRow('app_user', String(u.id), u);
    for (const [path, body] of Object.entries(responses)) this.store.cacheResponse(path, body);
    for (const [id, f] of Object.entries(files)) this.store.putBlob({ attachmentId: id, mimeType: f.type, byteSize: 0, sha256: '', localPath: `demo-file:${id}`, origin: 'downloaded' });
    try {
      this.deltas = JSON.parse(sessionStorage.getItem(DELTAS_KEY) ?? '[]') as Delta[];
    } catch {
      this.deltas = [];
    }
    for (const d of this.deltas) {
      if ('patch' in d) this.store.patchRow(d.patch.entity, d.patch.id, d.patch.data);
      else this.engine.apply(d.rows, d.deletes);
    }
  }

  private remember(d: Delta) {
    this.deltas.push(d);
    try {
      sessionStorage.setItem(DELTAS_KEY, JSON.stringify(this.deltas));
    } catch {
      /* too big or private mode: the change still holds until the tab closes */
    }
  }

  /** Back to the clinic as exported. */
  reset() {
    try {
      sessionStorage.removeItem(DELTAS_KEY);
    } catch {
      /* nothing kept */
    }
  }

  get changeCount(): number {
    return this.deltas.length;
  }

  private signIn() {
    const me = structuredClone(this.data.me);
    this.store.setSession({ userId: this.data.snapshot.userId, email: DEMO_LOGIN.email, verifier: '', me, cookies: {}, signedInAt: new Date().toISOString(), offlineSession: true });
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch {
      /* fine */
    }
  }

  private signOut() {
    this.store.setSession(null);
    try {
      sessionStorage.removeItem(SESSION_KEY);
    } catch {
      /* fine */
    }
  }

  async handle(method: string, url: URL, bodyText: string | null, locale: 'en' | 'ar'): Promise<DemoResponse> {
    const path = url.pathname;
    const json = (status: number, body: unknown): DemoResponse => ({ status, type: 'application/json', body: body === undefined ? null : JSON.stringify(body) });
    let body: unknown = null;
    if (bodyText) {
      try {
        body = JSON.parse(bodyText);
      } catch {
        body = null;
      }
    }

    // The demo's own gate: one published account, no second step.
    if (path === '/api/auth/login' && method === 'POST') {
      const b = (body ?? {}) as { email?: string; password?: string };
      if (String(b.email ?? '').trim().toLowerCase() !== DEMO_LOGIN.email || b.password !== DEMO_LOGIN.password) {
        return json(401, { code: 'invalid_credentials', message: locale === 'ar' ? `في النسخة التجريبية: ${DEMO_LOGIN.email} وكلمة المرور ${DEMO_LOGIN.password}` : `In the demo, sign in as ${DEMO_LOGIN.email} with the password “${DEMO_LOGIN.password}”.` });
      }
      this.signIn();
      return json(200, this.store.session()!.me);
    }
    if (path === '/api/auth/logout' && method === 'POST') {
      this.signOut();
      return { status: 204, type: 'text/plain', body: null };
    }
    if (path === '/api/health' || path === '/api/health/live') return json(200, { status: 'ok', demo: true });
    if (path.startsWith('/api/local/')) return json(404, { code: 'not_found', message: 'Not found.' });

    // The exported images are static files next to the demo.
    const file = path.match(/^\/api\/imaging\/file\/([0-9a-f-]{36})$/)?.[1];
    if (file && method === 'GET' && this.data.files[file] && this.store.session()) {
      const r = await fetch(`${this.base}data/${this.data.files[file].file}`);
      return { status: r.status, type: this.data.files[file].type, body: await r.blob() };
    }

    const res = await this.api.handle({ method, path, query: url.searchParams, body, locale });
    if (res.status === 503) {
      // A read the engine does not build: the answer recorded at export.
      const hit = method === 'GET' ? (this.store.cachedResponse(path + url.search) ?? this.store.cachedResponse(path)) : null;
      if (hit) return { status: 200, type: 'application/json', body: hit.body };
      return json(503, { code: 'demo_read_only', message: READ_ONLY[locale] });
    }
    // A posted invoice has its number at once (see DemoEngine.record).
    const posted = path.match(/^\/api\/finance\/invoices\/([0-9a-f-]{36})\/post$/)?.[1];
    if (posted && res.status === 200) {
      const inv = this.store.getRow('invoice', posted);
      return json(200, { id: posted, number: inv?.number ?? null, alreadyPosted: false });
    }
    if (res.bytes) return { status: res.status, type: res.contentType ?? 'application/octet-stream', body: res.bytes };
    return res.body === undefined ? { status: res.status, type: 'text/plain', body: null } : json(res.status, res.body);
  }
}
